import { jsx as _jsx, jsxs as _jsxs } from "theme-ui/jsx-runtime";
import { wrap } from "@popmotion/popcorn";
import { AnimatePresence, motion } from "framer-motion";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Box, Flex } from "theme-ui";
import { Label3, FontWeight, ImageVariants, zIndices, SmallArrowLeft, SmallArrowRight } from "../../index";
// style variants for arrows
const overlayStyles = {
    position: "absolute",
    right: 16,
    bottom: 0,
    pb: 32
};
const underStyles = {
    display: "relative"
};
const overlayBottomStyles = Object.assign(Object.assign({}, overlayStyles), { right: [16, 16, "80px"], bottom: [16, 16, 40], padding: 0, height: "auto", "& button": {
        width: [40, 40, 24],
        height: [40, 40, 24],
        backgroundColor: "grey.0",
        svg: {
            position: "relative",
            top: [0, 0, -1]
        },
        path: {
            fill: "white"
        }
    } });
// style variants for page indicator
const pageIndicatorDefaultStyles = {
    mr: 8,
    position: "absolute",
    right: 16,
    top: 32,
    zIndex: zIndices.inPage
};
const pageIndicatorBottomStyles = Object.assign(Object.assign({}, pageIndicatorDefaultStyles), { right: "auto", top: "auto", left: [16, 16, "80px"], bottom: [22, 22, 40] });
// swipe props
const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
};
export const Gallery = ({ id, children, ui_arrows = "overlay", dragDisabled = false, keyEventsDisabled = false, currentPage = 0, afterPageChange = () => { }, beforePageChange = () => { }, pageIndicatorMode = "top", style = {}, ratio = ImageVariants.ratio_1x1 }) => {
    const slideTotal = React.Children.count(children);
    const GallerySlides = React.Children.toArray(children).map((child, key) => {
        return _jsx(Fragment, { children: child }, key);
    });
    const vars = useRef({
        pageInitialized: false
    });
    const { current: v } = vars;
    // gallery state actions
    const galleryRef = useRef(null);
    const containerWidth = galleryRef.current
        ? galleryRef.current.getBoundingClientRect().width
        : "100%";
    const [[page, direction], setPage] = useState([currentPage, 1]);
    // gallery slide movements
    const imageIndex = wrap(0, slideTotal, page);
    const Slide = () => {
        if (GallerySlides[imageIndex]) {
            return GallerySlides[imageIndex];
        }
        else {
            // Must return null. Returning undefined crashes React
            return null;
        }
    };
    const paginate = (page, newDirection) => {
        beforePageChange();
        setPage([page + newDirection, newDirection]);
    };
    useEffect(() => {
        if (!v.pageInitialized) {
            v.pageInitialized = true;
            return;
        }
        afterPageChange();
    }, [page]);
    useEffect(() => {
        const handleArrows = (event) => {
            var _a;
            // accessibility: stop keyboard interactions with Gallery when user is moving within a radiogroup
            if (((_a = event.target) === null || _a === void 0 ? void 0 : _a.type) === "radio")
                return;
            if (event.code === "ArrowRight") {
                //forward
                paginate(page, 1);
            }
            if (event.code === "ArrowLeft") {
                // back
                paginate(page, -1);
            }
        };
        if (!keyEventsDisabled) {
            window.addEventListener("keydown", handleArrows);
        }
        return () => {
            window.removeEventListener("keydown", handleArrows);
        };
    }, [page, keyEventsDisabled]);
    let stylesToUse = {};
    if (ui_arrows === "overlay")
        stylesToUse = overlayStyles;
    else if (ui_arrows === "under")
        stylesToUse = underStyles;
    else if (ui_arrows === "overlay-bottom")
        stylesToUse = overlayBottomStyles;
    let pageInidicatorStyles = pageIndicatorMode === "bottom"
        ? pageIndicatorBottomStyles
        : pageIndicatorDefaultStyles;
    return (_jsx(Box, { sx: Object.assign({ width: "100%", variant: `images.${ratio}` }, style), children: _jsx(Box, { sx: {
                position: "absolute",
                top: 0,
                width: "100%",
                height: "100%",
                left: 0
            }, children: _jsxs(Box, { as: "section", "aria-roledescription": "carousel", sx: {
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    zIndex: zIndices.page,
                    flexDirection: "column-reverse",
                    ":focus": {
                        outline: "#eee solid 1px",
                        outlineOffset: "32px"
                    }
                }, children: [_jsx(Box, { sx: pageInidicatorStyles, children: _jsxs(Label3, { "aria-live": "polite", sx: { fontWeight: FontWeight.textBold }, children: [imageIndex + 1, " / ", slideTotal] }) }), _jsxs(Flex, { "aria-controls": `carousel-${id}`, sx: Object.assign({ height: [32, 32, "100%"], justifyContent: "center", alignItems: "center", zIndex: zIndices.page, button: {
                                bg: "white.0",
                                height: 32,
                                width: 32,
                                m: 0,
                                p: 0,
                                border: "none",
                                zIndex: zIndices.stickyBars,
                                path: {
                                    fill: "grey.0"
                                },
                                ":last-child": {
                                    ml: 2
                                },
                                ":hover": {
                                    bg: "black.1",
                                    path: {
                                        fill: "white.0"
                                    }
                                },
                                ":focus": {
                                    outline: "none",
                                    bg: "blue.0",
                                    path: {
                                        fill: "white.0"
                                    }
                                }
                            } }, stylesToUse), children: [_jsx(Box, { as: "button", tabIndex: 0, "aria-label": "Previous Slide", onClick: () => paginate(page, -1), children: _jsx(SmallArrowLeft, { "aria-hidden": "true", focusable: false }) }), _jsx(Box, { as: "button", tabIndex: 0, "aria-label": "Next Slide", onClick: () => paginate(page, 1), children: _jsx(SmallArrowRight, { "aria-hidden": "true", focusable: false }) })] }), _jsx(Flex, { role: "group", sx: {
                            flex: 1,
                            flexDirection: "column",
                            justifyItems: "center",
                            height: "auto",
                            position: "relative"
                        }, children: _jsx(Box, { as: "ul", sx: {
                                flex: 1,
                                listStyle: "none",
                                width: "100%",
                                height: "auto",
                                display: "flex",
                                position: "relative"
                            }, ref: galleryRef, id: `carousel-${id}`, children: _jsx(AnimatePresence, { initial: false, custom: { direction, width: containerWidth }, children: _jsx(motion.li, { custom: { direction, width: containerWidth }, variants: variants, initial: "enter", animate: "center", exit: "exit", transition: {
                                        x: { type: "spring", stiffness: 400, damping: 66 }
                                    }, style: {
                                        flex: 1,
                                        position: "absolute",
                                        width: "100%",
                                        height: "100%",
                                        willChange: "transform, opacity"
                                    }, drag: dragDisabled ? undefined : "x", dragConstraints: { left: 0, right: 0, top: 0, bottom: 0 }, dragElastic: 1, onDragEnd: (event, info) => {
                                        if (dragDisabled)
                                            return;
                                        const swipe = swipePower(info.offset.x, info.velocity.x);
                                        if (swipe < -swipeConfidenceThreshold) {
                                            paginate(page, 1);
                                        }
                                        else if (swipe > swipeConfidenceThreshold) {
                                            paginate(page, -1);
                                        }
                                    }, "aria-roledescription": "slide", "aria-label": `${imageIndex + 1} of ${slideTotal}`, children: _jsx(Slide, {}) }, page) }) }) })] }) }) }));
};
const variants = {
    enter: (props) => {
        return {
            x: props.direction > 0 ? props.width : -props.width,
            opacity: 0
        };
    },
    center: {
        zIndex: 1,
        x: 0,
        opacity: 1
    },
    exit: (props) => {
        return {
            zIndex: 0,
            x: props.direction < 0 ? props.width : -props.width,
            opacity: 0
        };
    }
};
//# sourceMappingURL=Gallery.js.map